import useMobileDetect from "@/hooks/useMobileDetect";
import { Button, Drawer, message as messageToast, Space } from "antd";
import { useRouter } from "next/navigation";
import { CloseSmall } from "@icon-park/react";
import { clacPriceProps } from "../shopingCartComponent/CartList";
import {
  calculateShopingCart,
  deleteShopingCart,
  queryShopingCartNum,
  queryShopingCartsList,
  shopingCartNum,
  shopingCartValidate,
  toggleShopingCartSelected,
} from "@/service/carts";
import { useCallback, useEffect, useState } from "react";
import { PageResult } from "../common";
import userInfoStore from "@/store/userInfoStore";
import EmptyCart from "@/app/assets/carts/EmptyCart.png";
import shopCartStore from "@/store/shopCartStore";
import { CartItem } from "../shopingCartComponent/CartItem";
import Successful from "@/app/assets/carts/Successful.svg";
import { addGtag } from "@/utils/tools";
import { Event_Name, Login_Click } from "@/service/user/enum";
import { BravveSmartButton } from "../common/BravveSmartButton";

export type MiniShoppingCartsProps = {
  open: boolean;
  onClose: () => void;
  onSignIn: () => void;
};

export const MiniShoppingCarts: React.FC<MiniShoppingCartsProps> = ({
  open,
  onClose,
  onSignIn,
  ...props
}) => {
  const isMobile = useMobileDetect();
  const router = useRouter();
  const { userInfo } = userInfoStore();
  const { setShopCart, shopCartInfo } = shopCartStore();
  // 选中的购物车数据
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  // 计算之后的购物车价格
  const [clacPriceObj, setClacPriceObj] = useState<clacPriceProps>({
    itemSubTotal: 0,
    itemDiscount: 0,
    estimatedTotal: 0,
  });

  /**
   * 获取购物车列表数据
   */
  const queryCardList = useCallback(async () => {
    const { status, data } = await queryShopingCartsList({});
    if (status === 200) {
      setShopCart({ shopCardsData: data ? [...data] : [] });
    }
  }, [setShopCart]); // 根据实际依赖情况调整这里的依赖项，确保函数引用稳定

  /**
   * 查询购物车数量
   */
  const selectShopingCartNum = useCallback(async () => {
    const { status, data } = await queryShopingCartNum();
    console.log(data, "======");
    if (status === 200) {
      let count = data?.count || 0; // 返回购物车数量，默认值为0
      setShopCart({ count });
    }
  }, [setShopCart]);

  useEffect(() => {
    open && queryCardList();
    open && selectShopingCartNum();
  }, [open, queryCardList, selectShopingCartNum]);

  /**
   * 删除购物车回调
   */
  const deleteCartChange = () => {
    setShopCart({ count: shopCartInfo.count - 1 });
    queryCardList();
  };

  /**
   * Review Full Carts
   */
  const reviewFullCarts = async () => {
    router.push("/shoppingCarts");
    onClose();
  };

  /**
   * 提交购买
   */
  const checkOutSubmit = async () => {
    addGtag(Event_Name.login_click, {
      click_position: Login_Click.cart_sign_in,
    });
    if (!userInfo?.email) {
      onSignIn();
      return;
    }

    if (!selectedItems || !selectedItems.length) {
      return;
    }

    const { status, data } = await shopingCartValidate({
      cartIdList: selectedItems,
    });

    // @ts-ignore
    if (globalThis.gtag) {
      // 触发 view_item 事件
      const userInfoStr = globalThis?.localStorage?.getItem("userInfo");
      const userInfo = userInfoStr ? JSON.parse(userInfoStr) : {};

      const sItems = shopCartInfo?.shopCardsData.filter((item) =>
        selectedItems.includes(item.id),
      );
      // @ts-ignore
      globalThis.gtag("event", "begin_checkout", {
        user_id: userInfo?.id,
        value: clacPriceObj,
        items: JSON.stringify(
          sItems.map((item) => ({
            id: item.id,
            name: item.itemName,
            category: item.itemPic,
            price: item.price,
            quantity: item.itemCount,
          })),
        ),
      });

      // @ts-ignore
      globalThis.fbq('track', 'InitiateCheckout', {
        content_ids: sItems.map((item) => item.id),
        content_type: 'product',
        value: clacPriceObj.itemSubTotal, 
        currency: 'USD'
      });
    }

    if (status === 200) {
      onClose();
      // 跳转结算页面
      const queryString = new URLSearchParams({ type: "cart" }).toString();
      router.push(`/checkout?${queryString}`);
    }
  };

  return (
    <>
      <Drawer
        className=""
        title={
          <div
            className={
              "mb-10 text-[18px] font-bold leading-[26px] text-bravve-100"
            }
          >
            My Cart ({shopCartInfo.count})
          </div>
        }
        open={open}
        onClose={onClose}
        closeIcon={false}
        extra={
          <Space className="hover:cursor-pointer">
            <CloseSmall
              theme="filled"
              fill="#402C22"
              size={"2.8rem"}
              onClick={onClose}
            />
          </Space>
        }
        placement={isMobile ? "bottom" : "right"}
        height={isMobile ? "88%" : "100%"}
        width={isMobile ? "100%" : "540px"}
        styles={{
          header: {
            paddingRight: isMobile ? "1.6rem" : "1.6rem",
            paddingLeft: isMobile ? "1.6rem" : "1.6rem",
            borderBottom: "none",
          },
          body: {
            display: "flex",
            flexDirection: "column",
            paddingTop: "0px",
          },
          footer: {
            padding: "16px 12px",
            borderTop:
              shopCartInfo?.shopCardsData && shopCartInfo?.shopCardsData.length
                ? "1px" + " solid " + "#E5E6EB"
                : "none",
          },
        }}
        footer={
          <div className="">
            {!shopCartInfo?.shopCardsData ||
            !shopCartInfo?.shopCardsData.length ? (
              false
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-[14px] leading-[22px] text-bravve-100">
                    Subtotal：
                  </span>
                  <span className="text-[16px] font-medium leading-[24px] text-bravve-100">
                    ${clacPriceObj?.estimatedTotal}
                  </span>
                </div>
                {/* <div className="mt-[16px] flex items-center justify-between">
                  <span className="text-[14px] leading-[22px] text-bravve-sub-100">
                    You Save：
                  </span>
                  <span className="text-[16px] leading-[24px] text-bravve-sub-100">
                    -${clacPriceObj?.itemDiscount}
                  </span>
                </div> */}
                <div className="flex items-center justify-between">
                  <BravveSmartButton
                    type="default"
                    size="large"
                    className="mr-[6px] mt-[16px] w-full"
                    isNavigation={true}
                    onClick={reviewFullCarts}
                  >
                    Review Full Carts
                  </BravveSmartButton>
                  <BravveSmartButton
                    type="primary"
                    size="large"
                    className="ml-[6px] mt-[16px] w-full"
                    onClick={checkOutSubmit}
                  >
                    {userInfo?.email ? "Checkout Now" : "Login to checkout"}
                  </BravveSmartButton>
                </div>
              </>
            )}
          </div>
        }
      >
        {!shopCartInfo?.shopCardsData || !shopCartInfo?.shopCardsData.length ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div>
              <PageResult
                title="Your cart is empty"
                sub={
                  userInfo?.email
                    ? "Add your favorite items in it."
                    : "Sign in to see if you have saved items."
                }
                img={EmptyCart}
              />
              {userInfo?.email ? (
                <div className="flex items-center justify-center">
                  <BravveSmartButton
                    style={{ height: "42px", borderColor: "#402C22" }}
                    className="mt-[24px] w-[248px]"
                    isNavigation={true}
                    onClick={() => {
                      router.push("/");
                      onClose();
                    }}
                  >
                    Start shopping
                  </BravveSmartButton>
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <BravveSmartButton
                    style={{ height: "42px" }}
                    type="primary"
                    className="mt-[24px] w-[248px] bg-bravve-100"
                    onClick={onSignIn}
                  >
                    Sign In
                  </BravveSmartButton>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {shopCartInfo?.isNew ? (
              <div className="mb-24 flex items-center">
                <Successful width={24} />
                <span className="ml-[8px] text-[16px] font-medium leading-[24px] text-success-200">
                  Added product successfully
                </span>
              </div>
            ) : (
              false
            )}
            <CartItem
              cartData={shopCartInfo?.shopCardsData || []}
              apiCalculateShopingCart={calculateShopingCart}
              apiToggleShopingCartSelected={toggleShopingCartSelected}
              apiShopingCartNum={shopingCartNum}
              apiDeleteShopingCart={deleteShopingCart}
              clacPriceChange={(data) => setClacPriceObj(data)}
              selectItemChange={(data) => setSelectedItems(data)}
              updateCartChange={() => queryCardList()}
              deleteCartChange={() => deleteCartChange()}
              shopCartType="drawer"
            />
          </>
        )}
      </Drawer>
    </>
  );
};
